import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, Loader2, LogIn, LogOut, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, mediaSrc } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import AmbientSound from "@/components/journey/AmbientSound";

function MembersList({ cfg }) {
  if (!cfg?.members?.length)
    return <p className="text-sm text-emerald-100/60" data-testid="forum-members-empty">No members yet. Be the first to join.</p>;
  return (
    <ul className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-black/15" data-testid="forum-members-list">
      {cfg.members.map((m, i) => (
        <li key={`${m.name}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
          <span className="text-emerald-50">{m.name}</span>
          <span className="text-xs text-emerald-100/60">{m.designation_label}</span>
        </li>
      ))}
    </ul>
  );
}

function JoinForm({ cfg, onJoined }) {
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("psychologist");
  const [consent, setConsent] = useState(false);
  const [joinWhatsapp, setJoinWhatsapp] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !password || !consent) return toast.error("Please fill in all fields and tick consent");
    // pre-open window synchronously so popup blockers don't kick in after await
    const waPopup = joinWhatsapp ? window.open("about:blank", "_blank") : null;
    setBusy(true);
    try {
      const { data } = await api.post("/forum/join", {
        password, name: name.trim(), designation, consent, join_whatsapp: joinWhatsapp,
      });
      toast.success("Welcome to Joyful Minds Mental Health Forum!");
      setName(""); setPassword(""); setConsent(false); setJoinWhatsapp(false);
      onJoined();
      if (joinWhatsapp && data.whatsapp_group_link) {
        if (waPopup) waPopup.location.href = data.whatsapp_group_link;
        else window.open(data.whatsapp_group_link, "_blank");
      } else if (waPopup) {
        waPopup.close();
      }
    } catch (err) {
      if (waPopup) waPopup.close();
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  if (!cfg?.has_password)
    return <p className="rounded-xl border border-amber-200/30 bg-amber-300/10 p-3 text-sm text-amber-100" data-testid="forum-no-password">
      Joining is not open yet. The admin has not set a password. Please check back later.
    </p>;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forum-join-form">
      <p className="text-xs text-emerald-100/70">Please fill in the details.</p>
      <div className="space-y-1.5">
        <Label className="text-emerald-100/80">Join password (shared by the admin)</Label>
        <Input data-testid="forum-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-emerald-100/80">Name</Label>
        <Input data-testid="forum-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-emerald-100/80">Designation</Label>
        <RadioGroup value={designation} onValueChange={setDesignation} className="grid gap-2 sm:grid-cols-2">
          {Object.entries(cfg.designations || {}).map(([key, label]) => (
            <Label key={key} htmlFor={`des-${key}`} data-testid={`forum-designation-${key}`}
                   className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm ${designation === key ? "border-emerald-300/70 bg-emerald-400/10 text-white" : "border-white/10 bg-black/15 text-emerald-100/80"}`}>
              <RadioGroupItem id={`des-${key}`} value={key} className="border-emerald-300 text-emerald-300" />
              {label}
            </Label>
          ))}
        </RadioGroup>
      </div>
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/15 p-3 text-sm text-emerald-50/90">
        <Checkbox data-testid="forum-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-emerald-300 data-[state=checked]:bg-emerald-400 data-[state=checked]:text-emerald-950" />
        <span>Consent to join Joyful Minds Mental Health Forum (JMMHF). {cfg.texts?.join_consent || "I volunteer to be a member and understand that joining is free of cost and I can cancel anytime."}</span>
      </label>
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/15 p-3 text-sm text-emerald-50/90">
        <Checkbox data-testid="forum-join-whatsapp" checked={joinWhatsapp} onCheckedChange={(v) => setJoinWhatsapp(!!v)} className="mt-0.5 border-emerald-300 data-[state=checked]:bg-emerald-400 data-[state=checked]:text-emerald-950" />
        <span>Request to join the JMMHF WhatsApp group. (A group invite link will open.)</span>
      </label>
      <Button type="submit" data-testid="forum-join-submit" disabled={busy} className="h-11 w-full rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="mr-2 h-4 w-4" /> Join the community</>}
      </Button>
    </form>
  );
}

function CancelForm({ cfg, onCancelled }) {
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("psychologist");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !designation || !consent) return toast.error("Please fill in name, pick your designation and tick the cancel consent");
    setBusy(true);
    try {
      await api.post("/forum/cancel", { name: name.trim(), designation });
      toast.success("Your membership is cancelled at your request.");
      onCancelled();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forum-cancel-form">
      <div className="space-y-1.5">
        <Label className="text-emerald-100/80">Name</Label>
        <Input data-testid="forum-cancel-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-emerald-100/80">Designation</Label>
        <RadioGroup value={designation} onValueChange={setDesignation} className="grid gap-2 sm:grid-cols-2">
          {Object.entries(cfg?.designations || {}).map(([key, label]) => (
            <Label key={key} htmlFor={`des-cancel-${key}`} data-testid={`forum-cancel-designation-${key}`}
                   className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm ${designation === key ? "border-emerald-300/70 bg-emerald-400/10 text-white" : "border-white/10 bg-black/15 text-emerald-100/80"}`}>
              <RadioGroupItem id={`des-cancel-${key}`} value={key} className="border-emerald-300 text-emerald-300" />
              {label}
            </Label>
          ))}
        </RadioGroup>
      </div>
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/15 p-3 text-sm text-emerald-50/90">
        <Checkbox data-testid="forum-cancel-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-emerald-300 data-[state=checked]:bg-emerald-400 data-[state=checked]:text-emerald-950" />
        <span>{cfg?.texts?.cancel_consent || "I request to cancel my membership in this forum."}</span>
      </label>
      <Button type="submit" variant="destructive" disabled={busy} data-testid="forum-cancel-submit" className="h-11 w-full rounded-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="mr-2 h-4 w-4" /> Cancel membership</>}
      </Button>
    </form>
  );
}

function ActivityCard({ a, lang }) {
  const title = (lang === "ml" ? a.title_ml : a.title_en) || a.title_en;
  const desc = (lang === "ml" ? a.description_ml : a.description_en) || a.description_en;
  const dateStr = new Date(`${a.date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return (
    <article className="space-y-3 rounded-2xl border border-white/10 bg-black/15 p-4" data-testid={`activity-card-${a.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="font-display text-base text-white">{title}</h4>
        <span className="shrink-0 rounded-full bg-amber-200/15 px-3 py-0.5 text-xs text-amber-100">{dateStr}</span>
      </div>
      {desc && <p className="whitespace-pre-wrap text-sm text-emerald-50/85">{desc}</p>}
      {a.media?.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {a.media.map((m) => (
            <div key={m.id} className="overflow-hidden rounded-xl bg-black/40" data-testid={`activity-media-${m.id}`}>
              {m.kind === "video"
                ? <video src={m.url} controls preload="metadata" className="h-40 w-full bg-black object-cover" />
                : <img src={m.url} alt="" loading="lazy" className="h-40 w-full object-cover" />}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function UpcomingList({ items, lang }) {
  if (items.length === 0)
    return <p className="text-sm text-emerald-100/60" data-testid="activities-upcoming-empty">No upcoming activities yet. Check back soon.</p>;
  return (
    <ol className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-black/15" data-testid="activities-upcoming-list">
      {items.map((a, i) => {
        const title = (lang === "ml" ? a.title_ml : a.title_en) || a.title_en;
        const desc = (lang === "ml" ? a.description_ml : a.description_en) || a.description_en;
        const dateStr = new Date(`${a.date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        return (
          <li key={a.id} className="flex gap-3 px-4 py-3" data-testid={`activities-upcoming-item-${a.id}`}>
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-xs font-semibold text-emerald-200">{i + 1}</span>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-display text-base text-white">{title}</span>
                <span className="rounded-full bg-amber-200/15 px-2 py-0.5 text-xs text-amber-100">{dateStr}</span>
              </div>
              {desc && <p className="whitespace-pre-wrap text-sm text-emerald-50/85">{desc}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ActivitiesPanel() {
  const { lang } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/forum/activities").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], completed: [] })); }, []);
  if (!data) return <div className="h-32 animate-pulse rounded-xl bg-white/5" />;
  return (
    <Tabs defaultValue="upcoming" className="space-y-3" data-testid="forum-activities-panel">
      <TabsList className="flex w-full rounded-xl bg-black/25 p-1">
        <TabsTrigger value="upcoming" data-testid="activities-tab-upcoming" className="flex-1 rounded-lg px-3 py-1.5 text-xs text-emerald-100 data-[state=active]:bg-emerald-400 data-[state=active]:text-emerald-950 sm:text-sm">
          <CalendarDays className="mr-1 h-4 w-4" /> Upcoming ({data.upcoming.length})
        </TabsTrigger>
        <TabsTrigger value="completed" data-testid="activities-tab-completed" className="flex-1 rounded-lg px-3 py-1.5 text-xs text-emerald-100 data-[state=active]:bg-amber-200 data-[state=active]:text-emerald-950 sm:text-sm">
          <Sparkles className="mr-1 h-4 w-4" /> Completed ({data.completed.length})
        </TabsTrigger>
      </TabsList>
      <TabsContent value="upcoming" className="space-y-3">
        <UpcomingList items={data.upcoming} lang={lang} />
      </TabsContent>
      <TabsContent value="completed" className="space-y-3">
        {data.completed.length === 0 && <p className="text-sm text-emerald-100/60" data-testid="activities-completed-empty">No completed activities yet.</p>}
        {data.completed.map((a) => <ActivityCard key={a.id} a={a} lang={lang} />)}
      </TabsContent>
    </Tabs>
  );
}

export default function ForumPage() {
  const { settings } = useApp();
  const [cfg, setCfg] = useState(null);
  const refresh = () => api.get("/forum/config").then((r) => setCfg(r.data)).catch(() => setCfg({ error: true }));
  useEffect(() => { refresh(); }, []);

  const logoSrc = useMemo(() => mediaSrc(settings?.media?.logo), [settings]);
  const texts = cfg?.texts || {};
  const opportunities = (texts.opportunities || "").split("\n").filter(Boolean);

  return (
    <div className="relative z-10 min-h-screen" data-testid="forum-page">
      <header className="flex items-center justify-between px-5 py-4 sm:px-10">
        <Link to="/" data-testid="forum-back-link" className="flex items-center gap-2 text-sm text-emerald-100/80 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <AmbientSound />
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16">
        <section className="glass space-y-3 rounded-[2rem] p-5 text-center sm:space-y-4 sm:p-10" data-testid="forum-header">
          {logoSrc && (
            <span className="mx-auto block h-16 w-16 overflow-hidden rounded-full bg-white ring-2 ring-amber-200/40">
              <img src={logoSrc} alt="Joyful Minds" className="h-full w-full scale-[1.6] object-cover" />
            </span>
          )}
          <h1 className="font-display text-2xl font-semibold tracking-[0.08em] text-white sm:text-3xl md:text-4xl">
            {texts.title || "JOYFUL MINDS™ Mental Health Forum"}
          </h1>
          <p className="font-display text-lg italic text-amber-100">"{texts.tagline || "Together Let's Make A Change"}"</p>
          <p className="text-sm text-emerald-100/80">{texts.intro}</p>
          <p className="text-sm text-emerald-100/80">{texts.vision}</p>
        </section>

        <section className="glass mt-5 space-y-3 rounded-[2rem] p-6 sm:p-8">
          <p className="font-display text-lg text-white">{texts.opportunities_heading || "Opportunities in this group:"}</p>
          <ul className="space-y-2 text-sm text-emerald-50/90">
            {opportunities.map((line, i) => (<li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />{line}</li>))}
          </ul>
        </section>

        <section className="glass mt-5 rounded-[2rem] p-4 sm:p-6">
          <Tabs defaultValue="members">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1.5 rounded-2xl bg-black/20 p-1.5">
              <TabsTrigger value="members" data-testid="forum-tab-members" className="h-11 justify-center rounded-xl px-2 text-xs text-emerald-100 data-[state=active]:bg-emerald-400 data-[state=active]:text-emerald-950 sm:text-sm"><Users className="mr-1 h-4 w-4" /> List of members</TabsTrigger>
              <TabsTrigger value="activities" data-testid="forum-tab-activities" className="h-11 justify-center rounded-xl px-2 text-xs text-emerald-100 data-[state=active]:bg-amber-200 data-[state=active]:text-emerald-950 sm:text-sm"><CalendarDays className="mr-1 h-4 w-4" /> Activities</TabsTrigger>
              <TabsTrigger value="join" data-testid="forum-tab-join" className="h-11 justify-center rounded-xl px-2 text-xs text-emerald-100 data-[state=active]:bg-emerald-400 data-[state=active]:text-emerald-950 sm:text-sm"><LogIn className="mr-1 h-4 w-4" /> Join the community</TabsTrigger>
              <TabsTrigger value="cancel" data-testid="forum-tab-cancel" className="h-11 justify-center rounded-xl px-2 text-xs text-emerald-100 data-[state=active]:bg-emerald-400 data-[state=active]:text-emerald-950 sm:text-sm"><LogOut className="mr-1 h-4 w-4" /> Cancel membership</TabsTrigger>
            </TabsList>
            <TabsContent value="members" className="mt-4"><MembersList cfg={cfg} /></TabsContent>
            <TabsContent value="activities" className="mt-4"><ActivitiesPanel /></TabsContent>
            <TabsContent value="join" className="mt-4"><JoinForm cfg={cfg} onJoined={refresh} /></TabsContent>
            <TabsContent value="cancel" className="mt-4"><CancelForm cfg={cfg} onCancelled={refresh} /></TabsContent>
          </Tabs>
        </section>
      </main>
    </div>
  );
}
